"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import { Search, Trophy } from "lucide-react";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { CompetitionItem } from "./CompetitionItem";
import { CountryAccordionItem } from "./CountryAccordionItem";
import type { CompetitionListItem, CountryCompetitions } from "../types";
import { AdSlot } from "@/features/ads/AdSlot";
import type { LocaleCode } from "@/lib/labels/types";
interface FootballCompetitionsClientProps {
  countriesWithCompetitions: CountryCompetitions[];
  popularCompetitions: CompetitionListItem[];
  locale: LocaleCode;
}

/**
 * FootballCompetitionsClient — Interactive client component.
 * Renders search bar, popular competitions, and country accordion.
 */
export function FootballCompetitionsClient(t0) {
  const $ = _c(38);
  const {
    countriesWithCompetitions,
    popularCompetitions
  } = t0;
  const labels = useLabels();
  const nav = useNavigation();
  const [searchQuery, setSearchQuery] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expandedCountries, setExpandedCountries] = useState(t1);
  let t2;
  bb0: {
    if (!searchQuery.trim()) {
      t2 = countriesWithCompetitions;
      break bb0;
    }
    let t3;
    if ($[1] !== countriesWithCompetitions || $[2] !== searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      t3 = countriesWithCompetitions.map(group => {
        const countryMatches = group.country.name.toLowerCase().includes(query);
        const matchingCompetitions = group.competitions.filter(c => c.name.toLowerCase().includes(query) || c.shortName?.toLowerCase().includes(query));
        if (countryMatches) {
          return group;
        }
        if (matchingCompetitions.length > 0) {
          return {
            ...group,
            competitions: matchingCompetitions
          };
        }
        return null;
      }).filter(Boolean);
      $[1] = countriesWithCompetitions;
      $[2] = searchQuery;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = t3 as CountryCompetitions[];
  }
  const filteredCountries = t2;
  let t3;
  bb1: {
    if (!searchQuery.trim()) {
      t3 = popularCompetitions;
      break bb1;
    }
    let t4;
    if ($[4] !== popularCompetitions || $[5] !== searchQuery) {
      const query_0 = searchQuery.toLowerCase().trim();
      t4 = popularCompetitions.filter(c_0 => c_0.name.toLowerCase().includes(query_0) || c_0.shortName?.toLowerCase().includes(query_0));
      $[4] = popularCompetitions;
      $[5] = searchQuery;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = t4;
  }
  const filteredPopular = t3;
  let t4;
  bb2: {
    if (searchQuery.trim()) {
      let t5;
      if ($[7] !== filteredCountries) {
        t5 = new Set(filteredCountries.map(_temp));
        $[7] = filteredCountries;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t4 = t5;
      break bb2;
    }
    t4 = expandedCountries;
  }
  const effectiveExpanded = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = countryId => {
      setExpandedCountries(prev => {
        const next = new Set(prev);
        if (next.has(countryId)) {
          next.delete(countryId);
        } else {
          next.add(countryId);
        }
        return next;
      });
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const toggleCountry = t5;
  let t6;
  if ($[10] !== nav) {
    t6 = competitionId => nav.footballCompetition(competitionId);
    $[10] = nav;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const buildCompetitionHref = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AdSlot id="content-top" />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e => setSearchQuery(e.target.value);
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== labels.competitions.searchPlaceholder || $[16] !== searchQuery) {
    t10 = <div className="relative mb-8">{t8}<input type="text" placeholder={labels.competitions.searchPlaceholder} value={searchQuery} onChange={t9} className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" /></div>;
    $[15] = labels.competitions.searchPlaceholder;
    $[16] = searchQuery;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== buildCompetitionHref || $[19] !== filteredPopular || $[20] !== labels.competitions.popularCompetitions) {
    t11 = filteredPopular.length > 0 && <section className="mb-10"><h2 className="flex items-center gap-2 text-lg font-semibold mb-4"><Trophy className="h-5 w-5 text-primary" />{labels.competitions.popularCompetitions}</h2><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">{filteredPopular.map(competition => <CompetitionItem key={competition.id} competition={competition} href={buildCompetitionHref(competition.id)} />)}</div></section>;
    $[18] = buildCompetitionHref;
    $[19] = filteredPopular;
    $[20] = labels.competitions.popularCompetitions;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== labels.competitions.allByCountry) {
    t12 = <h2 className="text-lg font-semibold mb-4">{labels.competitions.allByCountry}</h2>;
    $[22] = labels.competitions.allByCountry;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== buildCompetitionHref || $[25] !== effectiveExpanded || $[26] !== filteredCountries || $[27] !== labels.competitions.noResults || $[28] !== searchQuery) {
    t13 = filteredCountries.length === 0 ? <p className="text-muted-foreground text-center py-8">{labels.competitions.noResults.replace("{query}", searchQuery)}</p> : <div className="space-y-1">{filteredCountries.map(group_0 => <CountryAccordionItem key={group_0.country.id} group={group_0} isExpanded={effectiveExpanded.has(group_0.country.id)} onToggle={() => toggleCountry(group_0.country.id)} buildHref={buildCompetitionHref} />)}</div>;
    $[24] = buildCompetitionHref;
    $[25] = effectiveExpanded;
    $[26] = filteredCountries;
    $[27] = labels.competitions.noResults;
    $[28] = searchQuery;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t12 || $[31] !== t13) {
    t14 = <section>{t12}{t13}</section>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <AdSlot id="content-bottom" />;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t14) {
    t16 = <div className="max-w-350 mx-auto px-4 py-8 space-y-8">{t7}{t10}{t11}{t14}{t15}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t14;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
}
function _temp(g) {
  return g.country.id;
}